import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { AlertCircle, ListFilter, Search, Tag, ShieldBan, ShieldCheck, ShieldAlert, Info } from 'lucide-react';
import { PageHeader } from '../components/ui/PageHeader';
import { StatTile } from '../components/ui/StatTile';
import { EmptyState } from '../components/ui/EmptyState';
import { SkeletonLoader } from '../components/SkeletonLoader';
import { networkDataCacheService, type NetworkDataCacheResult } from '../services/networkDataCache';
import { SpawnListType } from '../types/networkData';
import { ItemIcon } from '../components/ItemIcon';
import { Highlight } from '../components/Highlight';
import { RedisKeyInfo } from '../components/RedisKeyInfo';
import { CacheStatusIndicator } from '../components/CacheStatusIndicator';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useIsMobile } from '../hooks/useIsMobile';
import { cn } from '../utils/cn';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SpawnLimitEntry {
  id: string;
  listType: SpawnListType;
  isTag: boolean;
}

type ListTypeFilter = 'all' | 'BLACKLIST' | 'GREYLIST' | 'WHITELIST';

// ---------------------------------------------------------------------------
// List type badge
// ---------------------------------------------------------------------------

const listTypeConfig: Record<
  SpawnListType,
  { label: string; bg: string; text: string; icon: React.ComponentType<{ className?: string }> }
> = {
  BLACKLIST: {
    label: 'Blacklist',
    bg: 'bg-red-500/20',
    text: 'text-red-300',
    icon: ShieldBan,
  },
  GREYLIST: {
    label: 'Greylist',
    bg: 'bg-yellow-500/20',
    text: 'text-yellow-300',
    icon: ShieldAlert,
  },
  WHITELIST: {
    label: 'Whitelist',
    bg: 'bg-emerald-500/20',
    text: 'text-emerald-300',
    icon: ShieldCheck,
  },
};

const ListTypeBadge = ({ listType }: { listType: SpawnListType }) => {
  const config = listTypeConfig[listType];
  const Icon = config.icon;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap',
        config.bg,
        config.text
      )}
    >
      <Icon className="w-3 h-3 flex-shrink-0" />
      {config.label}
    </span>
  );
};

// ---------------------------------------------------------------------------
// Spawn limit row — uses ItemIcon inline mode for non-tag items to show
// both the item image and display name fetched from the atlas API.
// The raw item ID is shown separately for search/reference.
// ---------------------------------------------------------------------------

const SpawnLimitRow = ({
  entry,
  query,
  isMobile,
}: {
  entry: SpawnLimitEntry;
  query: string;
  isMobile: boolean;
}) => {
  if (isMobile) {
    return (
      <div className="px-3 py-3 border-b border-glass-200/10 hover:bg-glass-200/5 transition-colors">
        <div className="flex items-center gap-2">
          <div className="flex-1 min-w-0">
            {entry.isTag ? (
              <div className="flex items-center gap-2 min-w-0">
                <Tag className="w-5 h-5 text-purple-400 flex-shrink-0" />
                <span className="text-sm font-mono text-white truncate">
                  <Highlight text={entry.id} highlight={query} />
                </span>
              </div>
            ) : (
              <>
                <div className="flex items-center min-w-0">
                  <ItemIcon
                    clusterId="_network"
                    itemId={entry.id}
                    displayMode="inline"
                    className="w-6 h-6 object-contain flex-shrink-0"
                  />
                </div>
                <div className="text-xs text-glass-500 font-mono truncate mt-1">
                  <Highlight text={entry.id} highlight={query} />
                </div>
              </>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {entry.isTag && (
              <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-purple-500/15 text-purple-300">
                TAG
              </span>
            )}
            <ListTypeBadge listType={entry.listType} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-4 px-4 py-3 border-b border-glass-200/10 hover:bg-glass-200/5 transition-colors">
      <div className="flex-1 min-w-0">
        {entry.isTag ? (
          <div className="flex items-center gap-2 min-w-0">
            <Tag className="w-5 h-5 text-purple-400 flex-shrink-0" />
            <span className="text-sm font-mono text-white truncate">
              <Highlight text={entry.id} highlight={query} />
            </span>
          </div>
        ) : (
          <>
            <div className="flex items-center min-w-0">
              <ItemIcon
                clusterId="_network"
                itemId={entry.id}
                displayMode="inline"
                className="w-6 h-6 object-contain flex-shrink-0"
              />
            </div>
            <div className="text-xs text-glass-500 font-mono truncate mt-0.5">
              <Highlight text={entry.id} highlight={query} />
            </div>
          </>
        )}
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {entry.isTag && (
          <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-purple-500/15 text-purple-300">
            TAG
          </span>
        )}
        <ListTypeBadge listType={entry.listType} />
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Virtualized list
// ---------------------------------------------------------------------------

const VirtualizedSpawnList = ({
  entries,
  query,
  isMobile,
}: {
  entries: SpawnLimitEntry[];
  query: string;
  isMobile: boolean;
}) => {
  const parentRef = useRef<HTMLDivElement>(null);

  const virtualizer = useVirtualizer({
    count: entries.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => (isMobile ? 72 : 64),
    overscan: 20,
  });

  return (
    <div ref={parentRef} className="glass-card overflow-y-auto max-h-[70vh]">
      <div
        style={{
          height: `${virtualizer.getTotalSize()}px`,
          width: '100%',
          position: 'relative',
        }}
      >
        {virtualizer.getVirtualItems().map((virtualRow) => {
          const entry = entries[virtualRow.index];
          return (
            <div
              key={`${entry.id}-${entry.isTag ? 'tag' : 'item'}`}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: `${virtualRow.size}px`,
                transform: `translateY(${virtualRow.start}px)`,
              }}
            >
              <SpawnLimitRow entry={entry} query={query} isMobile={isMobile} />
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Legend card
// ---------------------------------------------------------------------------

const SpawnLimitLegend = () => (
  <div className="glass-card p-3 md:p-5">
    <div className="flex items-center gap-2 mb-3">
      <Info className="w-4 h-4 text-accent-300 flex-shrink-0" />
      <h3 className="text-sm font-semibold text-white">How Spawn Limits Work</h3>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 md:gap-3 text-sm">
      <div className="flex items-start gap-2 p-2 md:p-3 rounded-lg bg-glass-200/5 border border-glass-200/10">
        <div className="w-2 h-2 rounded-full bg-glass-400 mt-1.5 flex-shrink-0" />
        <div className="min-w-0">
          <p className="font-medium text-glass-300">Unlisted</p>
          <p className="text-xs text-glass-500">
            Any user with spawn permissions can spawn these within their daily limit.
          </p>
        </div>
      </div>
      <div className="flex items-start gap-2 p-2 md:p-3 rounded-lg bg-red-500/5 border border-red-500/10">
        <div className="w-2 h-2 rounded-full bg-red-400 mt-1.5 flex-shrink-0" />
        <div className="min-w-0">
          <p className="font-medium text-red-300">Blacklisted</p>
          <p className="text-xs text-glass-500">
            Can never be spawned by any non-staff user, regardless of privileges.
          </p>
        </div>
      </div>
      <div className="flex items-start gap-2 p-2 md:p-3 rounded-lg bg-yellow-500/5 border border-yellow-500/10">
        <div className="w-2 h-2 rounded-full bg-yellow-400 mt-1.5 flex-shrink-0" />
        <div className="min-w-0">
          <p className="font-medium text-yellow-300">Greylisted</p>
          <p className="text-xs text-glass-500">
            Only users with elevated spawning privileges can spawn these.
          </p>
        </div>
      </div>
      <div className="flex items-start gap-2 p-2 md:p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/10">
        <div className="w-2 h-2 rounded-full bg-emerald-400 mt-1.5 flex-shrink-0" />
        <div className="min-w-0">
          <p className="font-medium text-emerald-300">Whitelisted</p>
          <p className="text-xs text-glass-500">
            Normal limits apply; elevated privileges allow unlimited quantities.
          </p>
        </div>
      </div>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Main view
// ---------------------------------------------------------------------------

export function SpawnLimitsView() {
  useDocumentTitle('Spawn Limits');
  const isMobile = useIsMobile();

  const [entries, setEntries] = useState<SpawnLimitEntry[]>([]);
  const [redisKey, setRedisKey] = useState<string | null>(null);
  const [cachedAt, setCachedAt] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [listTypeFilter, setListTypeFilter] = useState<ListTypeFilter>('all');
  const [showTags, setShowTags] = useState(true);

  const fetchData = useCallback(async (forceRefresh = false) => {
    try {
      setLoading(true);
      setError(null);

      // Cached copy first (if any), then the fresh document when the background re-read lands.
      const applyNetwork = (result: NetworkDataCacheResult) => {
      setRedisKey(result.key);
      setCachedAt(result.cachedAt);

      if (result.data?.itemSpawnLimitConfig) {
        const config = result.data.itemSpawnLimitConfig;
        const itemEntries: SpawnLimitEntry[] = Object.entries(config.items || {}).map(
          ([id, listType]) => ({ id, listType, isTag: false })
        );
        const tagEntries: SpawnLimitEntry[] = Object.entries(config.tags || {}).map(
          ([id, listType]) => ({ id, listType, isTag: true })
        );
        const sortOrder: Record<SpawnListType, number> = {
          BLACKLIST: 0,
          GREYLIST: 1,
          WHITELIST: 2,
        };
        const combined = [...itemEntries, ...tagEntries].sort((a, b) => {
          const orderDiff = sortOrder[a.listType] - sortOrder[b.listType];
          if (orderDiff !== 0) return orderDiff;
          return a.id.localeCompare(b.id);
        });
        setEntries(combined);
      } else {
        setEntries([]);
      }
      };

      applyNetwork(await networkDataCacheService.get(forceRefresh, applyNetwork));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch network data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRefresh = useCallback(async () => {
    await fetchData(true);
  }, [fetchData]);

  const filteredEntries = useMemo(() => {
    let list = entries;

    if (!showTags) {
      list = list.filter((e) => !e.isTag);
    }

    if (listTypeFilter !== 'all') {
      list = list.filter((e) => e.listType === listTypeFilter);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter((e) => e.id.toLowerCase().includes(q));
    }

    return list;
  }, [entries, showTags, listTypeFilter, searchQuery]);

  const stats = useMemo(() => {
    const itemsOnly = entries.filter((e) => !e.isTag);
    const tagsOnly = entries.filter((e) => e.isTag);
    return {
      total: entries.length,
      items: itemsOnly.length,
      tags: tagsOnly.length,
      blacklisted: entries.filter((e) => e.listType === 'BLACKLIST').length,
      greylisted: entries.filter((e) => e.listType === 'GREYLIST').length,
      whitelisted: entries.filter((e) => e.listType === 'WHITELIST').length,
    };
  }, [entries]);

  const filterTabs: { key: ListTypeFilter; label: string; count: number }[] = [
    { key: 'all', label: 'All', count: stats.total },
    { key: 'BLACKLIST', label: 'Blacklist', count: stats.blacklisted },
    { key: 'GREYLIST', label: 'Greylist', count: stats.greylisted },
    { key: 'WHITELIST', label: 'Whitelist', count: stats.whitelisted },
  ];

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader eyebrow="Network Config" title="Item Spawn Limits" description="Loading spawn limit configuration..." icon={ListFilter} />
        <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
          {Array.from({ length: 5 }).map((_, i) => <SkeletonLoader key={i} className="h-20" />)}
        </div>
        <div className="glass-card p-4 space-y-3">
          {Array.from({ length: 8 }).map((_, i) => <SkeletonLoader key={i} className="h-9" />)}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <PageHeader eyebrow="Network Config" title="Item Spawn Limits" icon={ListFilter} />
        <EmptyState tone="danger" icon={AlertCircle} title="Failed to load spawn limits" description={error} />
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      <PageHeader
        eyebrow="Network Config"
        title="Item Spawn Limits"
        description="Network-wide item spawn blacklist, greylist, and whitelist configuration."
        icon={ListFilter}
        actions={
          <>
            <CacheStatusIndicator cachedAt={cachedAt} onRefresh={handleRefresh} />
            <RedisKeyInfo redisKey={redisKey} />
          </>
        }
      />

      {/* Legend */}
      <SpawnLimitLegend />

      {/* Stats summary */}
      <div className={cn(
        'grid gap-3 md:gap-4',
        isMobile ? 'grid-cols-3' : 'grid-cols-3 md:grid-cols-5'
      )}>
        <StatTile label={<>Total</>} value={stats.total.toLocaleString()} tone="neutral" />
        <StatTile label={<>Blacklisted</>} value={stats.blacklisted} tone="danger" />
        <StatTile label={<>Greylisted</>} value={stats.greylisted} tone="warning" />
        <StatTile label={<>Whitelisted</>} value={stats.whitelisted} tone="neutral" />
        <StatTile label={<>Tags</>} value={stats.tags} tone="purple" />
      </div>

      {/* Search + filter bar */}
      <div className="flex flex-col gap-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-glass-400" />
          <input
            type="text"
            placeholder="Search by item or tag ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="glass-input w-full pl-10"
          />
        </div>
        <div className="flex gap-1 flex-wrap items-center">
          {filterTabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setListTypeFilter(tab.key)}
              className={cn(
                'px-2.5 md:px-3 py-1.5 md:py-2 rounded-lg text-xs md:text-sm font-medium transition-colors whitespace-nowrap',
                listTypeFilter === tab.key
                  ? 'bg-accent-500/15 text-accent-300 border border-accent-400/30'
                  : 'text-glass-300 hover:bg-glass-200/10 hover:text-white border border-transparent'
              )}
            >
              {tab.label}
              <span className="ml-1 text-xs opacity-60">({tab.count})</span>
            </button>
          ))}
          <div className="w-px h-5 bg-glass-200/20 mx-1" />
          <button
            onClick={() => setShowTags((prev) => !prev)}
            className={cn(
              'px-2.5 md:px-3 py-1.5 md:py-2 rounded-lg text-xs md:text-sm font-medium transition-colors whitespace-nowrap flex items-center gap-1.5',
              showTags
                ? 'bg-purple-500/15 text-purple-300 border border-purple-400/30'
                : 'text-glass-300 hover:bg-glass-200/10 hover:text-white border border-transparent'
            )}
          >
            <Tag className="w-3.5 h-3.5" />
            Tags
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="space-y-2">
        <p className="text-sm text-glass-300">
          Showing {filteredEntries.length.toLocaleString()} of {entries.length.toLocaleString()} entries
        </p>
        {filteredEntries.length === 0 ? (
          <div className="glass-card p-8 text-center text-glass-400">
            No spawn limit entries match your filters.
          </div>
        ) : (
          <VirtualizedSpawnList entries={filteredEntries} query={searchQuery} isMobile={isMobile} />
        )}
      </div>
    </div>
  );
}
